import { useCallback, useEffect, useRef, useState } from "react";
import { applyQuietUpdate } from "../utils/liveUpdates.js";

/**
 * Carga de datos con estados de carga y error.
 *
 * `loader` debe venir memorizado con `useCallback` por quien llama: es lo que
 * determina cuándo se recarga. Así las dependencias quedan declaradas en la
 * pantalla, que es donde se entienden, y no dentro del hook.
 *
 * Distingue dos situaciones que antes se confundían:
 *
 * - `loading`: todavía no hay nada que enseñar. Corresponde un indicador de
 *   carga a pantalla completa.
 * - `refreshing`: ya hay datos en pantalla y se están pidiendo otros —al
 *   filtrar, al buscar, al recargar—. Ahí vaciar la lista para poner un reloj
 *   es peor que dejarla: se pierde el sitio, la página da un salto y el ojo
 *   tiene que volver a encontrarlo todo. Lo que corresponde es atenuar lo que
 *   ya está mientras llega el relevo.
 *
 * La distinción se deduce comparando la petición que produjo lo que hay en
 * pantalla con la que toca ahora; no se enciende un `loading` desde dentro del
 * efecto, que provocaría un render de más en cada carga.
 */
export function useApi(loader, initialData = null) {
  const [state, setState] = useState(() => ({
    data: initialData,
    error: null,
    /** Petición que produjo estos datos. */
    origen: initialData === null ? null : loader,
    token: initialData === null ? -1 : 0,
  }));
  const [reloadToken, setReloadToken] = useState(0);

  // Copia de a qué petición corresponde el render actual, para que `setData`
  // pueda sellar los datos que le entregan sin que parezcan de una anterior.
  const actual = useRef({ loader, token: reloadToken });
  const revision = useRef(0);
  const backgroundPending = useRef(false);

  useEffect(() => {
    const context = { loader, token: reloadToken, active: true };
    actual.current = context;
    const observedRevision = revision.current;
    let cancelled = false;
    // `setData` seals newer data for this same request. A slow response that
    // started earlier must not overwrite a change made while it was loading.
    const superseded = () => cancelled || observedRevision !== revision.current;

    loader().then(
      (data) => {
        if (!superseded())
          setState({ data, error: null, origen: loader, token: reloadToken });
      },
      (error) => {
        if (!superseded())
          setState((previous) => ({
            // Si una actualización falla, se conserva la última respuesta
            // válida. Una caída transitoria no debe convertir una página que
            // ya estaba funcionando en una pantalla vacía.
            data: previous.data,
            error,
            origen: loader,
            token: reloadToken,
          }));
      },
    );

    return () => {
      cancelled = true;
      context.active = false;
    };
  }, [loader, reloadToken]);

  /** Recarga manual: se dispara desde un evento, nunca desde el render. */
  const reload = useCallback(() => {
    revision.current += 1;
    setReloadToken((token) => token + 1);
  }, []);

  // Polling never changes the loading token: keep the visible page interactive.
  // Discard responses superseded by navigation, filters, edits or manual reloads.
  const refreshQuietly = useCallback(async () => {
    if (backgroundPending.current) return;
    const context = actual.current;
    const observedRevision = revision.current;
    backgroundPending.current = true;
    try {
      const data = await context.loader();
      if (!context.active || context !== actual.current || observedRevision !== revision.current) return;
      setState((previous) => {
        if (!context.active || context !== actual.current || observedRevision !== revision.current) return previous;
        return applyQuietUpdate(previous, data, context);
      });
    } catch {
      // A failed background check must not interrupt the page; retry next tick.
    } finally {
      backgroundPending.current = false;
    }
  }, []);

  const setData = useCallback((data) => {
    revision.current += 1;
    const context = actual.current;
    setState((previous) => ({
      data: typeof data === "function" ? data(previous.data) : data,
      error: null,
      origen: context.loader,
      token: context.token,
    }));
  }, []);

  const enCurso = state.origen !== loader || state.token !== reloadToken;

  return {
    data: state.data,
    // `error` bloquea solo cuando nunca hubo datos. Una actualización fallida
    // queda disponible por separado para las pantallas que quieran avisarla
    // sin desmontar su contenido.
    error: state.data === null ? state.error : null,
    refreshError: state.data !== null ? state.error : null,
    loading: enCurso && state.data === null,
    refreshing: enCurso && state.data !== null,
    reload,
    refreshQuietly,
    setData,
  };
}
